import React, { useState, useEffect, useCallback, useRef } from 'react';
import Map, { Marker, Popup, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import Supercluster from 'supercluster';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/map.css';

const PropertyMap = ({ onPropertySelect, selectedProperty }) => {
  const [viewport, setViewport] = useState({
    latitude: 51.5074, // London default
    longitude: -0.1278,
    zoom: 11
  });
  
  const [properties, setProperties] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [cursor, setCursor] = useState('auto');
  const mapRef = useRef();
  const superclusterRef = useRef();

  // Initialize supercluster
  useEffect(() => {
    superclusterRef.current = new Supercluster({
      radius: 40,
      maxZoom: 16,
      map: (props) => ({ 
        price: props.asking_price,
        yield: props.gross_yield 
      }),
      reduce: (accumulated, props) => {
        accumulated.price = Math.min(accumulated.price || Infinity, props.price);
        accumulated.yield = Math.max(accumulated.yield || 0, props.yield || 0);
      }
    });
  }, []);

  // Fetch properties based on map bounds
  const fetchProperties = useCallback(async (bounds) => {
    try {
      const [sw, ne] = [bounds.getSouthWest(), bounds.getNorthEast()];
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await fetch(
        `${API_BASE}/api/v1/properties/geojson?` + 
        `sw_lng=${sw.lng}&sw_lat=${sw.lat}&ne_lng=${ne.lng}&ne_lat=${ne.lat}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch');
      const geojson = await response.json();
      
      // Load into supercluster
      superclusterRef.current.load(geojson.features);
      updateClusters();
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  // Update clusters based on current viewport
  const updateClusters = useCallback(() => {
    if (!mapRef.current || !superclusterRef.current) return;
    
    const bounds = mapRef.current.getMap().getBounds();
    const zoom = Math.floor(viewport.zoom);
    
    const clusters = superclusterRef.current.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      zoom
    );
    
    setClusters(clusters);
  }, [viewport.zoom]);

  // Fetch properties when viewport changes (debounced)
  useEffect(() => {
    if (!mapRef.current) return;
    
    const timer = setTimeout(() => {
      const bounds = mapRef.current.getMap().getBounds();
      fetchProperties(bounds);
    }, 300); // Debounce 300ms
    
    return () => clearTimeout(timer);
  }, [viewport.latitude, viewport.longitude, viewport.zoom, fetchProperties]);

  const handleMarkerClick = (property) => {
    onPropertySelect(property);
  };

  const renderCluster = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const { cluster: isCluster, point_count: pointCount } = cluster.properties;
    
    if (isCluster) {
      return (
        <Marker key={`cluster-${cluster.id}`} longitude={longitude} latitude={latitude}>
          <div 
            className="cluster-marker"
            style={{
              width: `${20 + (pointCount / properties.length) * 40}px`,
              height: `${20 + (pointCount / properties.length) * 40}px`
            }}
            onClick={() => {
              const expansionZoom = Math.min(
                superclusterRef.current.getClusterExpansionZoom(cluster.id),
                20
              );
              mapRef.current?.flyTo({
                center: [longitude, latitude],
                zoom: expansionZoom,
                duration: 500
              });
            }}
          >
            {pointCount}
          </div>
        </Marker>
      );
    }

    const property = cluster.properties;
    return (
      <Marker key={property.id} longitude={longitude} latitude={latitude}>
        <div 
          className="property-marker"
          onClick={() => handleMarkerClick(property)}
        >
          <span className="marker-price">£{(property.asking_price / 1000).toFixed(0)}k</span>
          {property.gross_yield && (
            <span className="marker-yield">{property.gross_yield.toFixed(1)}%</span>
          )}
        </div>
      </Marker>
    );
  };

  return (
    <div className="map-container">
      <Map
        ref={mapRef}
        initialViewState={viewport}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/satellite-v9" // Satellite style
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        onMove={(evt) => setViewport(evt.viewState)}
        onMouseEnter={() => setCursor('pointer')}
        onMouseLeave={() => setCursor('auto')}
        cursor={cursor}
      >
        {/* Dark mode overlay for professional look */}
        <div className="map-dark-overlay" />
        
        {/* Render clusters/markers */}
        {clusters.map(renderCluster)}
        
        {/* Selected property popup */}
        {selectedProperty?.location && (
          <Popup
            longitude={selectedProperty.location.longitude}
            latitude={selectedProperty.location.latitude}
            anchor="bottom"
            onClose={() => onPropertySelect(null)}
            closeButton={true}
          >
            <div className="popup-content">
              <h3>{selectedProperty.address.line_1}</h3>
              <p>£{selectedProperty.financials.asking_price.toLocaleString()}</p>
              <button onClick={() => onPropertySelect(selectedProperty)}>
                View Analysis
              </button>
            </div>
          </Popup>
        )}
      </Map>
      
      {/* Map controls */}
      <div className="map-controls">
        <button onClick={() => mapRef.current?.flyTo({ zoom: viewport.zoom + 1 })}>
          +
        </button>
        <button onClick={() => mapRef.current?.flyTo({ zoom: viewport.zoom - 1 })}>
          -
        </button>
      </div>
    </div>
  );
};

export default PropertyMap;
